import { Info } from '@phosphor-icons/react';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';

import { useCliJobChecker } from 'cli/hooks';
import { ICliJob } from 'cli/types';
import { Button } from 'core/components';
import { GetOneResponse } from 'core/types';
import { QuoteEntityType, QuoteNoteType } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';
import { getQuoteNoteContent } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { CustomerQuoteDetails } from './CustomerQuoteDetails';

interface IProps {
  quote: ICustomerQuote;
  pendingOrderCliJob?: ICliJob;
  quoteEntityType: QuoteEntityType;
  setQuote: (quote: ICustomerQuote) => void;
}

export const CustomerQuoteDetailsButton = ({
  quote,
  pendingOrderCliJob,
  quoteEntityType = QuoteEntityType.QUOTE,
  setQuote,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [inProgressPendingOrderCliJob, setInProgressPendingOrderCliJob] = useState<ICliJob>();
  const [pendingOrderCliJobResult, setPendingOrderCliJobResult] =
    useState<Partial<ICustomerQuote>>();

  useCliJobChecker({
    criteria: { job: inProgressPendingOrderCliJob?.id },
    enabled: !!inProgressPendingOrderCliJob,
    onSuccess: (data: GetOneResponse<ICliJob>) => {
      const { orders } = data.record.result as Record<string, ICustomerQuote[]>;

      setInProgressPendingOrderCliJob(undefined);
      setPendingOrderCliJobResult(orders.find((order) => order.id === quote.id));
    },
    onError: () => setInProgressPendingOrderCliJob(undefined),
  });

  const combinedQuote = useMemo(() => {
    return { ...quote, ...pendingOrderCliJobResult };
  }, [quote, pendingOrderCliJobResult]);

  const loadingFields = useMemo(() => {
    if (inProgressPendingOrderCliJob) {
      return {
        externalOrderNumber: true,
        externalPendingOrderNumber: true,
      };
    }

    return {};
  }, [inProgressPendingOrderCliJob]);

  useEffect(() => {
    if (pendingOrderCliJob) {
      setInProgressPendingOrderCliJob(pendingOrderCliJob);
    }
  }, [pendingOrderCliJob]);

  const badges = useMemo(() => {
    const badgeProps = [];

    if (getQuoteNoteContent(quote, QuoteNoteType.INTERNAL_NOTE)) {
      badgeProps.push({ key: QuoteNoteType.INTERNAL_NOTE });
    }

    if (getQuoteNoteContent(quote, QuoteNoteType.CUSTOMER_NOTE)) {
      badgeProps.push({ key: QuoteNoteType.CUSTOMER_NOTE, color: Color.DANGER });
    }

    return badgeProps;
  }, [quote]);

  return (
    <>
      <Button
        text={t('buttonLabelQuoteDetails', { entityType: quoteEntityType })}
        startDecorator={<Info />}
        variant={Variant.OUTLINED}
        onClick={() => setIsOpen(true)}
        badges={badges}
      />

      <CustomerQuoteDetails
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        initialQuote={combinedQuote}
        setQuote={setQuote}
        entityType={quoteEntityType}
        loadingFields={loadingFields}
      />
    </>
  );
};
